import { isObject } from 'lodash';
import { URGENT_VISIT_TYPE_CODE } from '../../constants';
import { APPOINTMENT_PROGRESS_TYPES } from './progress-types';

/**
 * @typedef {Object} AppointmentProgressState
 * @property {boolean} validating
 * @property {boolean} pending
 * @property {Object} careUser
 * @property {Object} organization
 * @property {Object} visitType
 * @property {Object} service
 * @property {Object} location
 * @property {Object} patientForm
 * @property {string} ssn
 * @property {string} reason
 * @property {Object} error
 * @property {Object} schedule
 * @property {boolean} isGWFlow
 * @property {string|null} pbvConfigId
 * @property {boolean} isUrgentVisitFlow
 * @property {Object} insuranceEligibility
 * @property {Object} rescheduleData
 * @property {Object} healthInfo
 * @property {string|null} pharmacyId
 * @property {Array} flowPages
 * @property {Object|null} pcpFormData
 * @property {AdditionalInfoFormData|null} additionalInfoFormData
 * */
const initialState = {
  validating: true,
  pending: false,
  careUser: null,
  organization: null,
  visitType: null,
  service: null,
  location: null,
  patientForm: null,
  ssn: null,
  reason: null,
  error: null,
  schedule: {},
  isGWFlow: false,
  pbvConfigId: null,
  isUrgentVisitFlow: false,
  insuranceEligibility: {},
  rescheduleData: {},
  healthInfo: null,
  pharmacyId: null,
  flowPages: [],
  pcpFormData: null, // used only for EIR visit type flow
  additionalInfoFormData: null,
  language: null, // used only for EIR visit type flow
};

/**
 * @param {AppointmentProgressState} state
 * */
export default function progress(state = initialState, { type, payload }) {
  switch (type) {
    case APPOINTMENT_PROGRESS_TYPES.VALIDATE: {
      return { ...state, validating: true };
    }

    case APPOINTMENT_PROGRESS_TYPES.VALIDATE_FINISH: {
      return { ...state, validating: false };
    }

    case APPOINTMENT_PROGRESS_TYPES.UPDATE_CARE_USER_PROFILE: {
      return { ...state, careUser: payload };
    }

    case APPOINTMENT_PROGRESS_TYPES.SET_CARE_USER: {
      return {
        ...state,
        careUser: payload,
        patientForm: null,
        organization: null,
        ssn: null,
        reason: null,
        schedule: {},
        pbvConfigId: null,
        insuranceEligibility: {},
        rescheduleData: {},
      };
    }

    case APPOINTMENT_PROGRESS_TYPES.SET_ORGANIZATION: {
      return { ...state, organization: payload };
    }

    case APPOINTMENT_PROGRESS_TYPES.SET_VISIT_TYPE: {
      return {
        ...state,
        visitType: payload,
        isUrgentVisitFlow: payload.code === URGENT_VISIT_TYPE_CODE,
      };
    }

    case APPOINTMENT_PROGRESS_TYPES.SET_SERVICE: {
      return { ...state, service: payload };
    }

    case APPOINTMENT_PROGRESS_TYPES.SET_LOCATION: {
      return { ...state, location: payload };
    }

    case APPOINTMENT_PROGRESS_TYPES.SET_PROGRAM_BASED_VISIT_ID: {
      return { ...state, pbvConfigId: payload.id };
    }

    case APPOINTMENT_PROGRESS_TYPES.CLEAR_PROGRAM_BASED_VISIT_ID: {
      return { ...state, pbvConfigId: null };
    }

    case APPOINTMENT_PROGRESS_TYPES.SET_PATIENT_INFO: {
      return { ...state, patientForm: payload };
    }

    case APPOINTMENT_PROGRESS_TYPES.SET_PREFERRED_PHARMACY: {
      return {
        ...state,
        pharmacyId: payload.id,
      };
    }

    case APPOINTMENT_PROGRESS_TYPES.SET_HEALTH_INFO: {
      const {
        ndcDrugIds,
        healthConditions,
        pcpProviderAnswer,
        medicationAllergies,
      } = payload;

      return {
        ...state,
        healthInfo: {
          ndcDrugIds: ndcDrugIds.map(el => (isObject(el) ? el.ndcDrugId : el)),
          pcpProviderAnswer,
          healthConditionIds: healthConditions.map(el => el.id),
          medicationAllergyIds: medicationAllergies.map(el => el.id),
        },
      };
    }

    case APPOINTMENT_PROGRESS_TYPES.SET_HAVE_CARE_PROVIDER_ANSWER: {
      const { pcpProviderAnswer } = payload;

      return {
        ...state,
        healthInfo: {
          pcpProviderAnswer,
        },
      };
    }

    case APPOINTMENT_PROGRESS_TYPES.SET_SSN: {
      return { ...state, ssn: payload };
    }

    case APPOINTMENT_PROGRESS_TYPES.SET_RESCHEDULE_DATA: {
      return {
        ...state,
        rescheduleData: payload,
      };
    }

    case APPOINTMENT_PROGRESS_TYPES.SET_REASON: {
      return { ...state, reason: payload };
    }

    case APPOINTMENT_PROGRESS_TYPES.SET_SCHEDULE_DATA: {
      return { ...state, schedule: payload };
    }

    case APPOINTMENT_PROGRESS_TYPES.SET_ERROR: {
      return { ...state, error: payload };
    }

    case APPOINTMENT_PROGRESS_TYPES.CLEAR: {
      return initialState;
    }

    case APPOINTMENT_PROGRESS_TYPES.SET_CREATE_TYPE_FLOW_GW: {
      return {
        ...state,
        isGWFlow: payload,
      };
    }

    case APPOINTMENT_PROGRESS_TYPES.SET_INSURANCE_ELIGIBILITY: {
      return {
        ...state,
        insuranceEligibility: payload,
      };
    }

    case APPOINTMENT_PROGRESS_TYPES.CLEAR_INSURANCE_ELIGIBILITY: {
      return {
        ...state,
        insuranceEligibility: {},
      };
    }

    case APPOINTMENT_PROGRESS_TYPES.SET_PENDING: {
      return {
        ...state,
        pending: payload,
      };
    }

    case APPOINTMENT_PROGRESS_TYPES.SET_FLOW_PAGES: {
      const newFlowPages = Array.from(new Set([...state.flowPages, payload]));

      return {
        ...state,
        flowPages: newFlowPages,
      };
    }

    case APPOINTMENT_PROGRESS_TYPES.REMOVE_FLOW_PAGE: {
      const newFlowPages = state.flowPages.filter(page => page !== payload);

      return {
        ...state,
        flowPages: newFlowPages,
      };
    }

    case APPOINTMENT_PROGRESS_TYPES.SET_PRIMARY_CARE_PROVIDER_FORM_DATA: {
      return {
        ...state,
        pcpFormData: payload,
      };
    }

    case APPOINTMENT_PROGRESS_TYPES.SET_ADDITIONAL_INFO_FORM_DATA: {
      return {
        ...state,
        additionalInfoFormData: payload,
      };
    }

    case APPOINTMENT_PROGRESS_TYPES.SET_PREFERRED_LANGUAGE: {
      return {
        ...state,
        language: payload,
      };
    }

    default: {
      return state;
    }
  }
}



// WEBPACK FOOTER //
// ./src/redux/appointmentForm/progress.js